import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
interface PrivacyPolicyPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyPolicyPopup({
  isOpen,
  onClose
}: PrivacyPolicyPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" data-id="element-232">
      <div ref={popupRef} className="bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-700" data-id="element-233">
        <div className="sticky top-0 bg-gray-900 text-white p-4 flex items-center justify-between z-10 border-b border-amber-700" data-id="element-234">
          <h2 className="text-xl font-bold text-amber-400" data-id="element-235">Privacy Policy</h2>
          <button onClick={onClose} className="text-white hover:text-amber-300 transition" aria-label="Close" data-id="element-236">
            <X size={24} data-id="element-237" />
          </button>
        </div>
        <div className="p-6 text-gray-300" data-id="element-238">
          <h3 className="text-lg font-semibold mb-4 text-amber-300" data-id="element-239">
            Introduction
          </h3>
          <p className="mb-4" data-id="element-240">
            This Privacy Policy explains how we collect, use, disclose, and
            safeguard your information when you visit our website or purchase
            our eBook "The Charlie Kirk Method." Please read this privacy policy
            carefully. By accessing or using our services, you acknowledge that
            you have read, understood, and agree to be bound by all the terms
            outlined in this privacy policy.
          </p>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-241">
            Information We Collect
          </h3>
          <p className="mb-2" data-id="element-242">
            <strong data-id="element-243">Personal Data:</strong> We may collect personal
            identification information, including but not limited to your name,
            email address, mailing address, phone number, and payment
            information when you purchase our book or sign up for our
            newsletter.
          </p>
          <p className="mb-4" data-id="element-244">
            <strong data-id="element-245">Usage Data:</strong> We may also collect information on how
            the website is accessed and used. This data may include your
            computer's IP address, browser type, browser version, pages visited,
            time spent on those pages, and other diagnostic data.
          </p>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-246">
            How We Use Your Information
          </h3>
          <ul className="list-disc pl-6 mb-4" data-id="element-247">
            <li className="mb-1" data-id="element-248">To process and fulfill your eBook order</li>
            <li className="mb-1" data-id="element-249">To provide customer support</li>
            <li className="mb-1" data-id="element-250">
              To send you promotional materials and newsletters (if you've opted
              in)
            </li>
            <li className="mb-1" data-id="element-251">To improve our website and services</li>
            <li className="mb-1" data-id="element-252">To comply with legal obligations</li>
          </ul>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-253">
            Disclosure of Your Information
          </h3>
          <p className="mb-4" data-id="element-254">
            We may share your information with third parties only in the ways
            described in this privacy policy, including:
          </p>
          <ul className="list-disc pl-6 mb-4" data-id="element-255">
            <li className="mb-1" data-id="element-256">
              With service providers who assist us in operating our website and
              conducting our business
            </li>
            <li className="mb-1" data-id="element-257">
              To comply with legal requirements, such as a law, regulation,
              court order, or subpoena
            </li>
            <li className="mb-1" data-id="element-258">
              To protect our rights, privacy, safety, or property
            </li>
          </ul>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-259">
            Cookies and Tracking Technologies
          </h3>
          <p className="mb-4" data-id="element-260">
            We use cookies and similar tracking technologies to track activity
            on our website and store certain information. You can instruct your
            browser to refuse all cookies or to indicate when a cookie is being
            sent.
          </p>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-261">
            Your Rights
          </h3>
          <p className="mb-4" data-id="element-262">
            Depending on your location, you may have certain rights regarding
            your personal information, including:
          </p>
          <ul className="list-disc pl-6 mb-4" data-id="element-263">
            <li className="mb-1" data-id="element-264">
              The right to access information we have about you
            </li>
            <li className="mb-1" data-id="element-265">
              The right to correct any inaccurate information
            </li>
            <li className="mb-1" data-id="element-266">
              The right to request deletion of your personal information
            </li>
            <li className="mb-1" data-id="element-267">
              The right to opt-out of marketing communications
            </li>
          </ul>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-268">
            Security of Your Information
          </h3>
          <p className="mb-4" data-id="element-269">
            We use administrative, technical, and physical security measures to
            protect your personal information. However, no method of
            transmission over the Internet or electronic storage is 100% secure,
            so we cannot guarantee absolute security.
          </p>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-270">
            Changes to This Privacy Policy
          </h3>
          <p className="mb-4" data-id="element-271">
            We may update our Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last Updated" date.
          </p>
          <h3 className="text-lg font-semibold mb-4 mt-6 text-amber-300" data-id="element-272">
            Contact Us
          </h3>
          <p className="mb-4" data-id="element-273">
            If you have any questions about this Privacy Policy, please contact
            us through our website.
          </p>
        </div>
        <div className="border-t border-gray-700 p-4 flex justify-end" data-id="element-274">
          <button onClick={onClose} className="bg-amber-600 hover:bg-amber-700 text-white font-medium py-2 px-4 rounded transition" data-id="element-275">
            Close
          </button>
        </div>
      </div>
    </div>;
}